import { useEffect, useState } from "react";

import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";

import {
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";
import { useFuzzySearch } from "@hooks/use-fuzzy-search";

import { useSalesSavedViews } from "../sales-saved-views-provider";

import { ReorderSavedViews } from "./reorder-saved-views";
import { SavedViewsList } from "./saved-views-list";

interface Props {
  buttonClassName?: string;
  placement?: "bottom" | "bottom-start";
}

export const SavedViewsMenu = ({ buttonClassName, placement }: Props) => {
  const { savedViews } = useSalesSavedViews();
  const [isReordering, setIsReordering] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const closeMenu = () => setIsMenuOpen(false);

  const [filteredOptions, { search, setSearch, clearSearch }] = useFuzzySearch(
    savedViews,
    {
      searchKeys: ["name"],
    },
  );

  const { refs, floatingStyles, context } = useFloating({
    open: isMenuOpen,
    onOpenChange: async (isOpen) => setIsMenuOpen(isOpen),
    transform: false,
    placement: placement ?? "bottom",
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  useEffect(() => {
    if (isMenuOpen) {
      clearSearch();
      setIsReordering(false);
    }
  }, [isMenuOpen, clearSearch]);

  return (
    <div>
      <button
        className={clsx(
          buttonClassName
            ? buttonClassName
            : "shrink-0 rounded-full border h-8 w-8 text-sm font-bold",
          {
            "text-primary hover:bg-cosmic-3": !isMenuOpen,
            "text-white bg-cosmic border-cosmic": isMenuOpen,
          },
        )}
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        <motion.i
          initial={false}
          animate={{ rotate: isMenuOpen ? 0 : 180 }}
          transition={{ duration: 0.2 }}
          className="fa-regular fa-sharp fa-chevron-up font-bold"
        />
      </button>
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            transition={{ ease: "easeOut", duration: 0.2 }}
            initial={{ opacity: 0, translateY: "0.25rem" }}
            animate={{ opacity: 1, translateY: 0 }}
            exit={{ opacity: 0, translateY: "0.25rem" }}
            ref={refs.setFloating}
            style={floatingStyles}
            className={clsx(
              "z-10 bg-white rounded-lg",
              "elevation-1 text-sm pt-1 pb-2 w-[300px]",
            )}
            {...getFloatingProps()}
          >
            {isReordering ? (
              <ReorderSavedViews
                isReordering={isReordering}
                cancelReordering={() => setIsReordering(false)}
                closeMenu={closeMenu}
                isMenuOpen={isMenuOpen}
              />
            ) : (
              <SavedViewsList
                savedViews={filteredOptions}
                closeMenu={closeMenu}
                enableReordering={() => setIsReordering(true)}
                search={search}
                setSearch={setSearch}
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
